<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader />
  </div>
  <div v-else class="view account-type-view">
    <div class="at-header">
      <h1 class="at-question">First, which account type fits you?</h1>
      <p class="at-sub">You can always change this later in settings.</p>
    </div>

    <div class="at-cards">
      <div
        v-for="(account, index) in accountTypes"
        :key="account.type"
        class="at-card"
        :class="account.type"
        @click="selectAccount(account.type)"
        @mouseenter="iconRefs[index]?.animate()"
      >
        <span v-if="account.badge" class="at-badge">{{ account.badge }}</span>

        <div class="icon-wrap">
          <component
            :is="account.iconComponent"
            :ref="(el) => (iconRefs[index] = el)"
          />
        </div>

        <p class="at-name">{{ account.name }}</p>
        <p class="at-primary">{{ account.primaryDetail }}</p>
        <p class="at-secondary">{{ account.secondaryDetail }}</p>
        <button class="at-btn" @click.stop="selectAccount(account.type)">
          {{ account.cta }}
        </button>
      </div>
    </div>

    <ProgressBar
      :currentStep="0"
      :steps="4"
      :stepLabels="['Account Type', 'Your Details', 'Categories', 'All Done']"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
// import { useMyStore } from '../../store.js';
import SkeletonLoader from '../../components/SkeletonLoader.vue';
import ProgressBar from '../../components/ProgressBar.vue';
import ShieldIcon from '../../components/icons/ShieldIcon.vue';
import SeedlingIcon from '../../components/icons/SeedlingIcon.vue';

// const store = useMyStore();
const router = useRouter();
const loading = ref(true);
const iconRefs = ref([]);

const accountTypes = [
  {
    name: 'Standard Account',
    primaryDetail: 'For anyone 18 and over.',
    secondaryDetail:
      'Connect your bank, sync transactions in real time, and take full control of your finances.',
    type: 'standard',
    badge: 'Most common',
    cta: 'Choose Standard',
    iconComponent: ShieldIcon,
  },
  {
    name: 'Junior Account',
    primaryDetail: "Under 18? This one's for you.",
    secondaryDetail:
      'A lighter account built for younger users. Upgrade to Standard automatically when you turn 18.',
    type: 'junior',
    badge: null,
    cta: 'Choose Junior',
    iconComponent: SeedlingIcon,
  },
];

const selectAccount = (type) => {
  if (type === 'standard') {
    router.push({ name: 'UserData' });
  } else {
    router.push({ name: 'JuniorUserData' });
  }
};

onMounted(() => {
  setTimeout(() => {
    const mainElement = document.querySelector('.main');
    if (mainElement) mainElement.scrollTo(0, 0);
  }, 100);
  loading.value = false;
});
</script>

<style scoped>
.account-type-view {
  max-width: 480px;
  margin: 0 auto;
}

.at-header {
  padding: 1.75rem 0 0.25rem;
  text-align: center;
}

.at-question {
  font-size: 1.3rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 0.35rem;
  letter-spacing: -0.02em;
}

.dark-theme .at-question {
  color: #5dcaa5;
}

.at-sub {
  font-size: 0.85rem;
  color: #888;
  margin: 0 0 1.75rem;
}

.at-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.at-card {
  border-radius: 16px;
  padding: 1.75rem 1.5rem 1.5rem;
  cursor: pointer;
  border: 2px solid #eee;
  background: white;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease;
}

.dark-theme .at-card {
  background: #212e3e;
  border-color: #2a3a4e;
}

.at-card.standard:hover {
  border-color: #7f77dd;
  box-shadow: 0 8px 32px rgba(127, 119, 221, 0.15);
  transform: translateY(-2px);
}

.at-card.junior:hover {
  border-color: #5dcaa5;
  box-shadow: 0 8px 32px rgba(93, 202, 165, 0.15);
  transform: translateY(-2px);
}

.at-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  letter-spacing: 0.03em;
  background: #eeedfe;
  color: #3c3489;
}

.icon-wrap {
  width: 80px;
  height: 80px;
  margin: 0 auto 1rem;
}

.at-name {
  font-size: 1.05rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 0.4rem;
}

.dark-theme .at-name {
  color: #ffffff;
}

.at-primary {
  font-size: 0.82rem;
  font-weight: 600;
  color: #555;
  margin: 0 0 0.4rem;
}

.dark-theme .at-primary {
  color: #aab;
}

.at-secondary {
  font-size: 0.78rem;
  color: #999;
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.dark-theme .at-secondary {
  color: #6a7a8a;
}

.at-btn {
  width: 100%;
  border: none;
  border-radius: 50px;
  padding: 0.65rem 1.5rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.at-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.at-btn:active {
  transform: scale(0.97);
}

.standard .at-btn {
  background: linear-gradient(135deg, #7f77dd, #5dcaa5);
  color: white;
}

.junior .at-btn {
  background: linear-gradient(135deg, #5dcaa5, #1d9e75);
  color: white;
}
</style>
